import { useState } from 'react';

/**
 * A product thumbnail.
 *
 * Two things this component is careful about, both of which are about the
 * screen never looking broken:
 *
 * **There is always something to draw.** Most list items have no photograph and
 * never will — an "any store" item has no variant, and a manual entry at a shop
 * with no adapter has no upstream product at all. So the picture is drawn *on
 * top of* a lettered tile rather than instead of it, and the tile is what shows
 * when there is no picture, when the picture has not arrived yet, and when it
 * fails to load. A grocery list where half the rows have a grey gap looks
 * broken; one where half the rows have a letter looks designed.
 *
 * **It is decorative, and says so.** The product name is always rendered right
 * next to it as real text, so an `alt` describing the product would make every
 * screen reader announce the same name twice. `alt=""` with the tile marked
 * `aria-hidden` is what makes that not happen — this was the reason to build it
 * as a component rather than repeat an `<img>` on four screens.
 *
 * The `src` is always a same-origin `/api/v1/images/<hash>` path (migration
 * 015). Never a shop's CDN: that is a request that fails in a supermarket with
 * no signal, needs four hosts added to the CSP, and tells four retailers what
 * this household is buying.
 */

/**
 * A stable colour per product, so the same yoghurt is the same colour on every
 * screen and after every restart. Hashed from the name rather than stored:
 * there is nothing to migrate, nothing to sync, and nothing to get out of step.
 *
 * The hues are spread around the wheel and the tile is drawn at low saturation
 * behind dark text — this is a background for a letter, not a colour anyone is
 * asked to interpret.
 */
function hueFor(text: string): number {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 360_000;
  }
  return hash % 360;
}

/**
 * The first letter, uppercased — of the first character that is actually a
 * letter or a digit, so "'t Zaanse" and "(bio) melk" do not render a tile
 * containing a punctuation mark.
 */
function initialOf(name: string): string {
  for (const char of name) {
    if (/\p{L}|\p{N}/u.test(char)) return char.toLocaleUpperCase('nl-NL');
  }
  return '?';
}

export interface ProductThumbProps {
  /** Same-origin image path, or null when this product has no picture. */
  src: string | null;
  /** Used for the letter and the colour. Never announced — see above. */
  name: string;
  /**
   * `list` is the row-sized default, `compare` the larger row on the
   * comparison screen, and `tile` the full width of a grid tile — which is the
   * whole reason the list became a grid. At 40px a packshot is a coloured
   * smudge; at the width of a tile it is the thing you match against a shelf.
   */
  size?: 'list' | 'compare' | 'tile';
}

export function ProductThumb({ src, name, size = 'list' }: ProductThumbProps) {
  /**
   * A failed image is remembered so the tile takes over cleanly. Without this
   * the browser draws its own broken-image glyph over the tile, which is the
   * one outcome worse than having no picture at all.
   *
   * Keyed on `src`, so a row re-used for a different product by React does not
   * inherit the previous product's failure.
   */
  const [failed, setFailed] = useState<string | null>(null);
  /**
   * Once a photograph is actually on screen the colour goes. The hue is the
   * letter tile's background; left behind a `contain`ed packshot it showed as
   * a mint, pink or cream frame that changed from card to card on one shelf
   * and meant nothing. A loaded photo sits on one neutral backdrop instead
   * (`.thumb-photo`), and the letter only ever shows when there is no photo.
   */
  const [loaded, setLoaded] = useState<string | null>(null);
  const photo = src !== null && loaded === src && failed !== src;

  const hue = hueFor(name);

  return (
    <span
      className={`thumb thumb-${size}${photo ? ' thumb-photo' : ''}`}
      aria-hidden="true"
      style={
        photo
          ? undefined
          : {
              backgroundColor: `hsl(${hue} 45% 88%)`,
              color: `hsl(${hue} 45% 28%)`,
            }
      }
    >
      {!photo && <span className="thumb-initial">{initialOf(name)}</span>}
      {src && failed !== src && (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(src)}
          onError={() => setFailed(src)}
        />
      )}
    </span>
  );
}
